/**
 * Centered modal shell used by every mentor-workspace dialog.
 *
 * `Alert.alert` is a no-op on web, and `window.confirm` cannot take typed input,
 * so anything that needs a field (finalize, manual session, email) uses this.
 */
import React from 'react';
import { Modal, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { mentorStyles } from './shared';

interface WorkspaceModalProps {
  visible: boolean;
  busy?: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
  testID?: string;
}

export function WorkspaceModal({
  visible,
  busy = false,
  title,
  subtitle,
  onClose,
  children,
  footer,
  maxWidth = 560,
  testID,
}: WorkspaceModalProps) {
  const { t } = useTranslation();
  const requestClose = () => { if (!busy) onClose(); };
  const { height } = useWindowDimensions();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={requestClose}>
      <View style={{
        flex: 1,
        backgroundColor: 'rgba(16, 24, 39, 0.45)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}>
        <View
          testID={testID}
          style={{
            width: '100%',
            maxWidth,
            maxHeight: height * 0.9,
            backgroundColor: reviewDesk.card,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: reviewDesk.line,
            overflow: 'hidden',
          }}
        >
          <View style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 12,
            padding: 18,
            borderBottomWidth: 1,
            borderBottomColor: reviewDesk.line,
          }}>
            <View style={mentorStyles.flexOne}>
              <Text style={mentorStyles.panelTitle}>{title}</Text>
              {subtitle ? <Text style={[mentorStyles.panelSubtitle, { marginTop: 4 }]}>{subtitle}</Text> : null}
            </View>
            <Pressable
              onPress={requestClose}
              disabled={busy}
              accessibilityState={{ disabled: busy }}
              accessibilityRole="button"
              accessibilityLabel={t('mentorEngagements.common.close')}
              style={({ pressed }) => [{ padding: 4 }, pressed && mentorStyles.pressed]}
            >
              <MaterialIcons name="close" size={22} color={reviewDesk.inkSoft} />
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 18, gap: 14 }}>
            {children}
          </ScrollView>

          {footer ? (
            <View style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 10,
              padding: 16,
              borderTopWidth: 1,
              borderTopColor: reviewDesk.line,
              backgroundColor: reviewDesk.cardMuted,
            }}>
              {footer}
            </View>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

export default WorkspaceModal;
